import {
  ChannelMessageDoc,
  IssueCase,
  IssueCaseCategory,
  IssueCaseCreatorType,
  IssueCaseDoc,
  IssueCaseKind,
  IssueCaseRequestCategory,
  IssueCaseStatusCode,
  IssueSourceType,
  OrderDoc,
} from '@gooduncles/gu-app-schema';
import { Alert, Form, Input, Modal, Segmented, Select, message as antdMessage } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';

import { issueCaseCategoryKr } from 'src/constants/issue.constants';
import { createIssueCase } from 'src/utils/firebase/firebase-short-cut';
import {
  SelectedMessageRef,
  getMessageBodyPreview,
  linkMessagesToIssueCase,
} from 'src/utils/issue/issue-case-message-link-util';
import { getIssueCaseInitialData, issueCaseIssuedByOptions, issueCaseSubOptions } from 'src/utils/issue/issue-util';

import useStoreById from 'src/hooks/swr/useStoreById';

import LinkedIssueCaseBadge from 'src/components/Crm/CrmBoard/components/CreateFormSection/LinkedIssueCaseBadge';
import LinkedOrderBadge from 'src/components/Crm/CrmBoard/components/CreateFormSection/LinkedOrderBadge';

import CaseSourcePicker from './CaseSourcePicker';

import classes from './CreateCaseFromMessagesModal.module.scss';

type AnyCategory = IssueCaseCategory | IssueCaseRequestCategory;

interface FormValues {
  category: AnyCategory;
  subcategory?: string;
  issuedBy: string;
  content?: string;
  privateNote: string;
  note?: string;
}

const ISSUE_CATEGORIES = new Set<string>(Object.values(IssueCaseCategory));
const REQUEST_CATEGORIES = new Set<string>(Object.values(IssueCaseRequestCategory));

const caseKindOptions: { label: string; value: IssueCaseKind }[] = [
  { label: '이슈', value: 'issue' },
  { label: '요청', value: 'request' },
];

interface CreateCaseFromMessagesModalProps {
  open: boolean;
  onClose: () => void;
  storeId: string;
  /** 선택된 메시지 (id/collection/messageCreatedAt). 미리보기는 messageDocsById 로 보완. */
  selectedMessages: SelectedMessageRef[];
  /** 미리보기/sender 표시용 — 키: messageId */
  messageDocsById: Record<string, ChannelMessageDoc | undefined>;
  /** 케이스 생성 + 링크 성공 시 호출 */
  onSuccess?: (caseId: string) => void;
}

const CreateCaseFromMessagesModal: FC<CreateCaseFromMessagesModalProps> = ({
  open,
  onClose,
  storeId,
  selectedMessages,
  messageDocsById,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [form] = Form.useForm<FormValues>();
  const [caseKind, setCaseKind] = useState<IssueCaseKind>('issue');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 연결할 원천(주문/이슈) — source 는 단일 필드라 둘 중 하나만 유지된다.
  const [linkedOrder, setLinkedOrder] = useState<OrderDoc | null>(null);
  const [linkedIssueCase, setLinkedIssueCase] = useState<IssueCaseDoc | null>(null);

  const { store, storeLoading: isLoadingStore } = useStoreById(open ? storeId : undefined);

  const selectedCategory = Form.useWatch('category', form);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setCaseKind('issue');
      setLinkedOrder(null);
      setLinkedIssueCase(null);
    }
  }, [open, form]);

  useEffect(() => {
    form.setFieldsValue({ category: undefined, subcategory: undefined });
  }, [caseKind, form]);

  useEffect(() => {
    form.setFieldValue('subcategory', undefined);
  }, [selectedCategory, form]);

  const categoryOptions = useMemo(() => {
    const validKeys = caseKind === 'issue' ? ISSUE_CATEGORIES : REQUEST_CATEGORIES;
    return Object.entries(issueCaseCategoryKr)
      .filter(([key]) => validKeys.has(key))
      .map(([value, label]) => ({ label, value }));
  }, [caseKind]);

  const subcategoryOptions = useMemo(() => {
    if (!selectedCategory) return [];
    return issueCaseSubOptions(selectedCategory) ?? [];
  }, [selectedCategory]);

  const handleSubmit = async () => {
    if (!user) {
      antdMessage.error('사용자 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }
    if (!store) {
      antdMessage.error('매장 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const source = (() => {
        if (linkedOrder) {
          return {
            sourceType: IssueSourceType.ORDER,
            sourceId: linkedOrder._id,
            sourceReferenceDate: linkedOrder.deliveryDate ?? null,
          };
        }
        if (linkedIssueCase) {
          return {
            sourceType: IssueSourceType.ISSUE,
            sourceId: linkedIssueCase._id,
            sourceReferenceDate: linkedIssueCase.registeredAt ?? null,
          };
        }
        return { sourceType: null, sourceId: null, sourceReferenceDate: null };
      })();

      const base = getIssueCaseInitialData({
        ...source,
        storeId: store._id,
        storeName: store.storeName,
        creatorId: user._id,
        creatorType: user.role as IssueCaseCreatorType,
        category: values.category,
        subcategory: (values.subcategory as any) ?? null,
        caseKind,
        content: caseKind === 'request' ? values.content ?? null : null,
      });

      const issueData: IssueCase = {
        ...base,
        issuedBy: values.issuedBy as any,
        publicNote: null,
        privateNote: values.privateNote ?? null,
      };

      const newCaseId = await createIssueCase(issueData);
      const linkResult = await linkMessagesToIssueCase(
        { _id: newCaseId, storeId: store._id, status: IssueCaseStatusCode.OPEN },
        selectedMessages,
        user._id,
        values.note?.trim() ? values.note.trim() : null,
      );

      if (linkResult.failureCount === 0) {
        antdMessage.success(`케이스 등록 + 메시지 ${linkResult.successCount}건 연결 완료`);
      } else {
        antdMessage.warning(
          `케이스는 등록되었으나 메시지 ${linkResult.failureCount}건 연결 실패. 케이스 상세에서 재시도해주세요.`,
        );
      }
      onSuccess?.(newCaseId);
      onClose();
    } catch (error) {
      // form.validateFields throws on validation error — antd already shows inline.
      if ((error as { errorFields?: unknown }).errorFields) return;
      console.error('[CreateCaseFromMessagesModal] submit failed', error);
      antdMessage.error('케이스 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formLabelCol = { flex: '0 0 70px' };
  const formWrapperCol = { flex: 1 };

  return (
    <Modal
      title='선택한 메시지로 케이스 등록'
      open={open}
      onCancel={onClose}
      width={1000}
      okText='케이스 등록'
      cancelText='취소'
      onOk={handleSubmit}
      confirmLoading={isSubmitting}
      destroyOnClose
    >
      <Alert
        type='info'
        showIcon
        className={classes.banner}
        message={
          isLoadingStore
            ? '매장 정보 로딩 중…'
            : store
              ? `매장: ${store.storeName} (${store._id})`
              : `매장을 찾을 수 없습니다 — storeId=${storeId}`
        }
      />

      <div className={classes.layout}>
        <div className={classes.leftCol}>
          <div className={classes.selectedMessages}>
            <div className={classes.sectionLabel}>선택한 메시지 {selectedMessages.length}건</div>
            <div className={classes.messageList}>
              {selectedMessages.map((ref) => {
                const doc = messageDocsById[ref.messageId];
                const body = doc?.body ?? null;
                return (
                  <div key={ref.messageId} className={classes.messageRow}>
                    <span className={classes.createdAt}>{dayjs(ref.messageCreatedAt).format('YYYY-MM-DD HH:mm')}</span>
                    {doc?.senderCollection && doc?.senderId && (
                      <span className={classes.sender}>
                        {doc.senderCollection}:{doc.senderId}
                      </span>
                    )}
                    <span className={classes.body}>
                      {getMessageBodyPreview(body, 120) || doc?.title || '(본문 없음)'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <Form
            form={form}
            labelCol={formLabelCol}
            wrapperCol={formWrapperCol}
            labelAlign='right'
            className={classes.form}
          >
            <Form.Item label='연결 대상'>
              {linkedOrder ? (
                <LinkedOrderBadge order={linkedOrder} onRemove={() => setLinkedOrder(null)} />
              ) : linkedIssueCase ? (
                <LinkedIssueCaseBadge issueCase={linkedIssueCase} onRemove={() => setLinkedIssueCase(null)} />
              ) : (
                <span className={classes.noLinked}>연결된 주문/이슈 없음 — 우측에서 선택</span>
              )}
            </Form.Item>

            <Form.Item label='유형'>
              <Segmented
                options={caseKindOptions}
                value={caseKind}
                onChange={(val) => setCaseKind(val as IssueCaseKind)}
                block
              />
            </Form.Item>

            <Form.Item
              name='issuedBy'
              label='출처'
              rules={[{ required: true, message: '필수' }]}
              initialValue='customer'
            >
              <Select options={issueCaseIssuedByOptions} placeholder='선택' />
            </Form.Item>

            <Form.Item name='category' label='분류' rules={[{ required: true, message: '필수' }]}>
              <Select options={categoryOptions} placeholder='선택' />
            </Form.Item>

            <Form.Item name='subcategory' label='상세분류'>
              <Select
                options={subcategoryOptions}
                placeholder='선택'
                disabled={!selectedCategory || subcategoryOptions.length === 0}
                allowClear
              />
            </Form.Item>

            {caseKind === 'request' && (
              <Form.Item name='content' label='요청 내용'>
                <Input.TextArea placeholder='내용 입력' rows={2} />
              </Form.Item>
            )}

            <Form.Item name='privateNote' label='내부 메모' rules={[{ required: true, message: '필수' }]}>
              <Input.TextArea placeholder='내용 입력' rows={2} />
            </Form.Item>

            <Form.Item name='note' label='연결 메모'>
              <Input.TextArea placeholder='메시지를 케이스에 묶는 이유 (선택)' rows={1} />
            </Form.Item>
          </Form>
        </div>

        <div className={classes.rightCol}>
          <CaseSourcePicker
            storeId={open ? storeId : null}
            linkedOrder={linkedOrder}
            linkedIssueCase={linkedIssueCase}
            onSelectOrder={setLinkedOrder}
            onSelectIssueCase={setLinkedIssueCase}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CreateCaseFromMessagesModal;
